import { useMemo } from "react";

import { useSelector } from "react-redux";

import { translate } from "app/filters/i18nFilter";
import { ControlledErrorBoundaryWithLogging } from "app/pages/sales-intelligence/common-components/ErrorBoundaryWithLogging";
import { errorCategoryMap, SiError } from "app/pages/sales-intelligence/utils/errors/types";
import ScrollToTopWrapper from "app/pages/sales-intelligence/common-components/ScrollToTopWrapper/ScrollToTopWrapper";
import { DualScoringBoardContainer } from "app/pages/sales-intelligence/modules/lead-scoring-agent/containers/DualScoringBoardContainer";
import { useScoringFiltersBootstrap } from "app/pages/sales-intelligence/modules/lead-scoring-agent/hooks/useScoringFiltersBootstrap";
import {
    selectSignalsAreBeingFetched,
    selectSignalsFetchError,
} from "app/pages/sales-intelligence/sub-modules/signals/store/selectors";
import { EmptyState } from "@similarweb/ui-components";

import { ScoringSettingsPageLoader } from "../components/ScoringSettingsPageLoader";
import ScoringSettingsPageHeader from "../components/ScoringSettingsPageHeader/ScoringSettingsPageHeader";
import {
    SCORE_SETTINGS_ADD_RULE_TRANSLATION_KEYS,
    SCORE_SETTINGS_SEGMENT_TRANSLATION_KEYS,
} from "../constants/scoreSettingsBoardTranslationKeys";

import { VisitsFromSingleCountryProvider } from "app/pages/sales-intelligence/modules/leads-search-filters/context/VisitsFromSingleCountryContext";
import { StyledScoringSettingsInnerContainer, StyledScoringSettingsPage } from "./styles";

export const ScoringSettingsPageContainer = () => {
    const signalsFetchError = useSelector(selectSignalsFetchError);
    const isSignalsFetching = useSelector(selectSignalsAreBeingFetched);

    const { filtersList, isBootstrapping } = useScoringFiltersBootstrap();

    const isPageLoading = isBootstrapping;
    const isError = !!signalsFetchError;

    const boundaryError = useMemo(
        () =>
            isError
                ? new SiError(
                      "Scoring settings signals request failed",
                      {
                          category: errorCategoryMap.api,
                          component: "ScoringSettingsPageContainer",
                          operation: "fetchSignals",
                      },
                      { cause: signalsFetchError },
                  )
                : undefined,
        [isError, signalsFetchError],
    );

    return (
        <ScrollToTopWrapper>
            <ControlledErrorBoundaryWithLogging
                error={boundaryError}
                resetKeys={[isSignalsFetching, signalsFetchError]}
                fallbackRender={() => (
                    <EmptyState
                        title={translate("common.error")}
                        subtitle={translate("si.common.error.reload")}
                        iconName="no-data-tree"
                        data-automation="score-settings-signals-error"
                    />
                )}
            >
                <VisitsFromSingleCountryProvider value>
                    <StyledScoringSettingsPage data-automation="score-criteria-page">
                        <StyledScoringSettingsInnerContainer
                            flexDirection="column"
                            gap="24px"
                        >
                            {isPageLoading ? (
                                <ScoringSettingsPageLoader />
                            ) : (
                                <>
                                    <ScoringSettingsPageHeader
                                        title={{ key: "si.pages.lead_scoring.title" }}
                                        description={{ key: "si.pages.score_settings.description" }}
                                    />

                                    <DualScoringBoardContainer
                                        filtersList={filtersList}
                                        segmentTranslationKeys={SCORE_SETTINGS_SEGMENT_TRANSLATION_KEYS}
                                        addRuleTranslationKeys={SCORE_SETTINGS_ADD_RULE_TRANSLATION_KEYS}
                                        automationPrefix="scoring-board"
                                        isExportEnabled
                                    />
                                </>
                            )}
                        </StyledScoringSettingsInnerContainer>
                    </StyledScoringSettingsPage>
                </VisitsFromSingleCountryProvider>
            </ControlledErrorBoundaryWithLogging>
        </ScrollToTopWrapper>
    );
};
